// The flattened view-model for a single search result. The `search` query returns a union
// (AppResourceUnion) whose members all expose name + path; we normalize them to one shape at the
// connected boundary so the dropdown can render + group them uniformly. `typename` is the GraphQL
// __typename (e.g. "ItemCodexRecord", "Collection", "Profile", "Topic"), which drives categorization.
// A codex sense (partOfSpeech + gloss), from CodexCardFields — the rich results page shows the first one.
export interface CodexSense {
  partOfSpeech?: string | null
  definition?: string | null
  translation?: string | null
}

export interface SearchHit {
  __typename: string
  id?: string | null
  name: string
  path: string
  // Human-readable type label from the server (e.g. "Codex record"), shown above the URL preview. Derived
  // from the class name via ApplicationRecord.ui_label; absent in the header dropdown (not selected there).
  classLabel?: string | null
  type?: string | null
  // Whether the current user has saved this resource (drives the results-page action menu's star).
  saved?: boolean | null
  // Codex records only: the primary translation/gloss, shown after the headword.
  translation?: string | null
  // Calendar event collections (CollectionCalendarEvent) only: the event date range, shown on the row/result.
  startsAt?: string | null
  endsAt?: string | null
  // Codex records only (results page): the rich meta from CodexCardFields — pronunciation, dialect, kind,
  // and senses — rendered as a Google-style rich result. Absent in the header dropdown.
  pronunciation?: string | null
  dialect?: string | null
  kind?: string | null
  senses?: CodexSense[] | null
  // Content items + collections: a short description, used as the results-page snippet. Absent in the
  // header dropdown (which doesn't select it) — components must treat it as optional.
  summary?: string | null
  thumbnailUrl?: string | null
  // Profiles only: the "@slug" handle and the avatar image URL (null when they have no icon). The
  // results page also pulls slug/createdAt/levelProgress so it can render the shared ProfileCard.
  username?: string | null
  slug?: string | null
  createdAt?: string | null
  avatar?: {
    imageUrl: string | null
  }
  banner?: {
    imageUrl: string | null
  } | null
  levelProgress?: {
    currentLevel?: number | null
  } | null
}

export type SearchCategory =
  | "Codex"
  | "Profiles"
  | "Collections"
  | "Events"
  | "Library"
  | "Topics"

// Coarse buckets for the dropdown. Codex records and the other Item subtypes share the ItemInterface
// but read very differently to a learner, so we split "Codex" (dictionary entries) from "Library"
// (books, audio, video, …). Anything we don't recognize falls back to Library.
export function categoryOf(typename: string): SearchCategory {
  switch (typename) {
    case "Profile":
      return "Profiles"
    case "Topic":
      return "Topics"
    case "Collection":
      return "Collections"
    case "CollectionCalendarEvent":
      return "Events"
    case "ItemCodexRecord":
      return "Codex"
    default:
      return "Library"
  }
}

// Display order of the grouped sections. Codex leads because it's the primary "look up a word" case.
export const CATEGORY_ORDER: SearchCategory[] = [
  "Codex",
  "Profiles",
  "Collections",
  "Events",
  "Library",
  "Topics",
]

export interface SearchGroup {
  category: SearchCategory
  hits: SearchHit[]
}

// Group hits by category, preserving CATEGORY_ORDER and dropping empty groups. Within a group the
// server's relevance/recency order is preserved (we don't re-sort).
export function groupHits(hits: SearchHit[]): SearchGroup[] {
  const byCategory = new Map<SearchCategory, SearchHit[]>()

  for (const hit of hits) {
    const category = categoryOf(hit.__typename)
    const bucket = byCategory.get(category) ?? []
    bucket.push(hit)
    byCategory.set(category, bucket)
  }

  return CATEGORY_ORDER.filter((category) => byCategory.has(category)).map(
    (category) => ({ category, hits: byCategory.get(category) as SearchHit[] })
  )
}
